/**
 * Transaction Health Page
 *
 * Manifold observability: transaction success/failure rates, daily trends,
 * gateway breakdown, error codes, and Chargebee vs Hyperswitch split.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts'
import { manifoldAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, CheckCircle, XCircle, DollarSign,
  Activity, TrendingUp, GitCompare
} from 'lucide-react'

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(cents / 100)
}

function AcceptanceBar({ rate, label }) {
  const color = rate >= 80 ? 'bg-green-500' : rate >= 60 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{rate}%</span>
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div className={`${color} rounded-full h-2 transition-all`} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
    </div>
  )
}

export default function TransactionHealthPage() {
  const [days, setDays] = useState(90)
  const [data, setData] = useState(null)
  const [splitData, setSplitData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [healthRes, splitRes] = await Promise.all([
        manifoldAPI.getTransactionHealth({ days }),
        manifoldAPI.getPaymentSplit({ days }),
      ])
      if (healthRes.data.success) setData(healthRes.data.data)
      if (splitRes.data.success) setSplitData(splitRes.data.data)
    } catch (err) {
      console.error('[TXN_HEALTH] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [days])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const summary = data?.summary
  const cb = splitData?.chargebee
  const hs = splitData?.hyperswitch

  // Map initiator labels
  const initiatorLabel = { merchant: 'Recurring', customer: 'New', untagged: 'Untagged' }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Transaction Health</h1>
          <p className="text-sm text-muted-foreground mt-1">Payment processing performance across Chargebee and Hyperswitch</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Activity className="w-3.5 h-3.5" /> Total Transactions
                </div>
                <div className="text-xl font-bold">{formatNumber(summary?.total)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" /> Success
                </div>
                <div className="text-xl font-bold text-green-600">{formatNumber(summary?.success)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <XCircle className="w-3.5 h-3.5 text-red-500" /> Failures
                </div>
                <div className="text-xl font-bold text-red-600">{formatNumber(summary?.failure)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Success Rate
                </div>
                <div className="text-xl font-bold">{summary?.success_rate}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5" /> Total Volume
                </div>
                <div className="text-xl font-bold">{formatCurrency(summary?.total_amount)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5 text-green-500" /> Collected
                </div>
                <div className="text-xl font-bold text-green-600">{formatCurrency(summary?.success_amount)}</div>
              </CardContent>
            </Card>
          </div>

          {/* ═══ Payment System Split ═══ */}
          {splitData && (
            <>
              <div className="flex items-center gap-2 pt-2">
                <GitCompare className="w-5 h-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">Chargebee vs Hyperswitch</h2>
              </div>

              {/* Side-by-side comparison cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Chargebee card */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Chargebee (NMI Gateway)</CardTitle>
                    <CardDescription>{formatNumber(cb?.totals?.total)} payments</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">Success</div>
                        <div className="text-lg font-bold text-green-600">{formatNumber(cb?.totals?.success)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Failed</div>
                        <div className="text-lg font-bold text-red-600">{formatNumber(cb?.totals?.failure)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Acceptance</div>
                        <div className="text-lg font-bold">{cb?.totals?.acceptance_rate}%</div>
                      </div>
                    </div>

                    <div className="border-t border-border pt-3 space-y-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">By Payment Type</div>
                      {cb?.by_initiator?.map((row) => (
                        <AcceptanceBar
                          key={row.initiator}
                          label={`${initiatorLabel[row.initiator] || row.initiator} (${formatNumber(row.total)})`}
                          rate={row.acceptance_rate}
                        />
                      ))}
                    </div>

                    <div className="border-t border-border pt-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Volume</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Total: </span>
                          <span className="font-medium">{formatCurrency(cb?.totals?.amount)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Collected: </span>
                          <span className="font-medium text-green-600">{formatCurrency(cb?.totals?.success_amount)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Hyperswitch card */}
                <Card className="border-blue-200 dark:border-blue-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      Hyperswitch (Cybersource)
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">A/B TEST</span>
                    </CardTitle>
                    <CardDescription>
                      {formatNumber(hs?.totals?.total)} payments
                      {hs?.totals?.pending > 0 && (
                        <span className="text-muted-foreground"> + {formatNumber(hs.totals.pending)} pending</span>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-xs text-muted-foreground">Succeeded</div>
                        <div className="text-lg font-bold text-green-600">{formatNumber(hs?.totals?.succeeded)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Failed</div>
                        <div className="text-lg font-bold text-red-600">{formatNumber(hs?.totals?.failed)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Acceptance</div>
                        <div className="text-lg font-bold">{hs?.totals?.acceptance_rate}%</div>
                      </div>
                    </div>

                    <div className="border-t border-border pt-3 space-y-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">By Payment Type</div>
                      {hs?.by_payment_type?.map((row) => (
                        <AcceptanceBar
                          key={row.payment_type}
                          label={`${row.payment_type === 'new' ? 'New' : 'Recurring'} (${formatNumber(row.total)})`}
                          rate={row.acceptance_rate}
                        />
                      ))}
                    </div>

                    {hs?.totals?.pending > 0 && (
                      <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2.5 py-1.5">
                        {formatNumber(hs.totals.pending)} partial sessions excluded (customer created on page load, no payment attempted)
                      </div>
                    )}

                    <div className="border-t border-border pt-3">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Volume</div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">Collected: </span>
                        <span className="font-medium text-green-600">{formatCurrency(hs?.totals?.succeeded_amount)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Chargebee daily by initiator */}
              {cb?.daily?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Chargebee Daily: New vs Recurring</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={cb.daily}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="recurring_success" stroke="#22c55e" strokeWidth={2} dot={false} name="Recurring Success" />
                          <Line type="monotone" dataKey="recurring_failure" stroke="#ef4444" strokeWidth={1.5} dot={false} name="Recurring Failure" strokeDasharray="4 2" />
                          <Line type="monotone" dataKey="new_success" stroke="#3b82f6" strokeWidth={2} dot={false} name="New Success" />
                          <Line type="monotone" dataKey="new_failure" stroke="#f59e0b" strokeWidth={1.5} dot={false} name="New Failure" strokeDasharray="4 2" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Hyperswitch daily */}
              {hs?.daily?.length > 0 && (
                <Card className="border-blue-200 dark:border-blue-800">
                  <CardHeader>
                    <CardTitle className="text-base">Hyperswitch Daily Volume (terminal only)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hs.daily}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="new_succeeded" stackId="a" fill="#22c55e" name="New Succeeded" />
                          <Bar dataKey="new_failed" stackId="a" fill="#ef4444" name="New Failed" />
                          <Bar dataKey="recurring_succeeded" stackId="a" fill="#15803d" name="Recurring Succeeded" />
                          <Bar dataKey="recurring_failed" stackId="a" fill="#b91c1c" name="Recurring Failed" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error comparison */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Chargebee Top Errors (by type)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left">
                            <th className="pb-2 font-medium text-muted-foreground">Error Code</th>
                            <th className="pb-2 font-medium text-muted-foreground">Type</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right">Count</th>
                            <th className="pb-2 font-medium text-muted-foreground">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cb?.top_errors?.map((err, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 font-mono text-xs text-red-600">{err.error_code}</td>
                              <td className="py-2 text-xs">
                                <span className={`px-1.5 py-0.5 rounded ${
                                  err.initiator === 'merchant' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                                  err.initiator === 'customer' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                  'bg-muted text-foreground'
                                }`}>
                                  {initiatorLabel[err.initiator] || err.initiator}
                                </span>
                              </td>
                              <td className="py-2 text-right">{formatNumber(err.count)}</td>
                              <td className="py-2 text-xs text-muted-foreground truncate max-w-36">{err.sample_text || '-'}</td>
                            </tr>
                          ))}
                          {(!cb?.top_errors || cb.top_errors.length === 0) && (
                            <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No errors</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-200 dark:border-blue-800">
                  <CardHeader>
                    <CardTitle className="text-base">Hyperswitch Errors (by type)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left">
                            <th className="pb-2 font-medium text-muted-foreground">Error Code</th>
                            <th className="pb-2 font-medium text-muted-foreground">Type</th>
                            <th className="pb-2 font-medium text-muted-foreground text-right">Count</th>
                            <th className="pb-2 font-medium text-muted-foreground">Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hs?.top_errors?.map((err, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 font-mono text-xs text-red-600">{err.error_code}</td>
                              <td className="py-2 text-xs">
                                <span className={`px-1.5 py-0.5 rounded ${
                                  err.payment_type === 'recurring' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                                  'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                }`}>
                                  {err.payment_type === 'recurring' ? 'Recurring' : 'New'}
                                </span>
                              </td>
                              <td className="py-2 text-right">{formatNumber(err.count)}</td>
                              <td className="py-2 text-xs text-muted-foreground truncate max-w-36">{err.sample_message || '-'}</td>
                            </tr>
                          ))}
                          {(!hs?.top_errors || hs.top_errors.length === 0) && (
                            <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No errors</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          {/* ═══ Original sections ═══ */}

          {/* Daily Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">All Chargebee Daily Volume (incl. refunds)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success" stroke="#22c55e" strokeWidth={2} dot={false} name="Success" />
                    <Line type="monotone" dataKey="failure" stroke="#ef4444" strokeWidth={2} dot={false} name="Failure" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Gateway */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Gateway</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Gateway</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Total</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Success</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Rate</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_gateway?.map((gw) => (
                        <tr key={gw.gateway} className="border-b border-border/50">
                          <td className="py-2 font-medium">{gw.gateway || '(none)'}</td>
                          <td className="py-2 text-right">{formatNumber(gw.total)}</td>
                          <td className="py-2 text-right text-green-600">{formatNumber(gw.success)}</td>
                          <td className="py-2 text-right">{gw.total > 0 ? ((gw.success / gw.total) * 100).toFixed(1) : 0}%</td>
                          <td className="py-2 text-right">{formatCurrency(gw.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Top Errors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Top Error Codes (All)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Error Code</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">Count</th>
                        <th className="pb-2 font-medium text-muted-foreground">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_errors?.map((err) => (
                        <tr key={err.error_code} className="border-b border-border/50">
                          <td className="py-2 font-mono text-xs text-red-600">{err.error_code}</td>
                          <td className="py-2 text-right">{formatNumber(err.count)}</td>
                          <td className="py-2 text-xs text-muted-foreground truncate max-w-48">{err.sample_text || '-'}</td>
                        </tr>
                      ))}
                      {(!data.top_errors || data.top_errors.length === 0) && (
                        <tr><td colSpan={3} className="py-4 text-center text-muted-foreground">No errors in this period</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* By Type */}
          {data.by_type && data.by_type.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Transaction Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.by_type}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value, name) => name === 'amount' ? formatCurrency(value) : formatNumber(value)} />
                      <Bar dataKey="count" fill="#6366f1" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
